import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { isMarketingBrowserExcluded } from "../lib/marketingAttribution";
import { marketingContext, configureMarketing, captureMetaClick } from "../lib/marketingPrivacy";
import { syncMetaPixel, trackMetaPageView } from "../lib/metaPixel";

export default function MetaMarketingConsent() {
  const { user } = useAuth();
  const location = useLocation();
  const [enabled, setEnabled] = useState(false);
  const [pixelId, setPixelId] = useState(null);
  const [preference, setPreference] = useState(marketingContext);
  const [excluded, setExcluded] = useState(isMarketingBrowserExcluded);
  const visit = useRef(null);
  const internal = excluded || user?.role === "admin" || user?.impersonated_by;

  useEffect(() => {
    let live = true;
    api.get("/public/marketing/meta-config", { timeout: 4000 })
      .then(({ data }) => { if (live) { configureMarketing(data); setEnabled(data.enabled === true); setPixelId(data.pixel_id || null); setPreference(marketingContext()); } }).catch(() => {});
    const sync = () => { setPreference(marketingContext()); setExcluded(isMarketingBrowserExcluded()); };
    window.addEventListener("marketing-preference", sync);
    window.addEventListener("storage", sync);
    return () => { live = false; window.removeEventListener("marketing-preference", sync); window.removeEventListener("storage", sync); };
  }, []);

  useEffect(() => {
    if (!enabled || !preference) return;
    // Also synchronize refusals/exclusion: queued server conversions must respect them.
    try { captureMetaClick(window.location); } catch { return; }
    let cancelled = false;
    let timer;
    const sync = () => api.post("/public/marketing/meta-consent", {}, { timeout: 4000 })
      .catch(() => { if (!cancelled) timer = setTimeout(sync, 15000); });
    sync();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [enabled, preference, excluded, location.search]);

  useEffect(() => {
    syncMetaPixel(pixelId, enabled && !internal && user === false && ["granted", "not_requested"].includes(preference?.choice)
      && ["/", "/creators"].includes(location.pathname));
    return () => syncMetaPixel(pixelId, false);
  }, [enabled, pixelId, internal, user, preference, location.pathname]);

  useEffect(() => {
    if (!enabled || internal || user !== false || !["granted", "not_requested"].includes(preference?.choice)
      || !["/", "/creators"].includes(location.pathname)) return;
    const key = location.pathname + location.search;
    if (visit.current?.key !== key) visit.current = { key, id: window.crypto.randomUUID(), sent: false };
    if (visit.current.sent) return;
    try { captureMetaClick(window.location); } catch { return; }
    visit.current.sent = true;
    trackMetaPageView(visit.current.id, location.pathname);
    api.post("/public/marketing/meta-pageview", { event_id: visit.current.id, path: location.pathname }, { timeout: 4000 }).catch(() => {});
  }, [enabled, internal, user, preference, location.pathname, location.search]);

  return null;
}
